import React, { useCallback, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

type HighlightPulseEffectProps = {
  children: React.ReactNode;
  setHighlightedCallback?: (cb: VoidFunction) => void;
  borderRadius?: number;
};

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.04);
  }
  100% {
    transform: scale(1);
  }
`;

const Wrapper = styled.span<{ $radius: number }>`
  display: inline-block;
  position: relative;
  border-radius: ${({ $radius }) => `${$radius}px`};
  will-change: transform, box-shadow;

  &.is-highlighted {
    animation: ${pulse} 700ms cubic-bezier(0.34, 1.56, 0.64, 1);
    /* softer, subtler glow */
    box-shadow:
      0 6px 12px rgba(0, 102, 78, 0.18),
      0 4px 10px rgba(0, 0, 0, 0.08),
      0 0 2px var(--primary-Primary, #006c4d);
  }

  /* 2px transparent spacer ring to offset the outer ring */
  &.is-highlighted::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: inherit;
    border: 2px solid transparent;
    pointer-events: none;
  }

  /* subtle green outer ring just beyond the spacer */
  &.is-highlighted::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: inherit;
    border: 4px solid #008862;
    box-shadow: 0 0 6px rgba(0, 102, 78, 0.25);
    pointer-events: none;
  }
`;

export const HighlightPulseEffect = ({
  children,
  setHighlightedCallback,
  borderRadius = 8,
}: HighlightPulseEffectProps): JSX.Element => {
  const ref = useRef<HTMLSpanElement | null>(null);

  const trigger = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove('is-highlighted');
    // reflow to restart animation
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    void el.offsetWidth;
    el.classList.add('is-highlighted');
    const handleEnd = () => {
      el.classList.remove('is-highlighted');
      el.removeEventListener('animationend', handleEnd);
    };
    el.addEventListener('animationend', handleEnd);
  }, []);

  useEffect(() => {
    if (setHighlightedCallback) setHighlightedCallback(trigger);
  }, [setHighlightedCallback, trigger]);

  return (
    <Wrapper ref={ref} $radius={borderRadius}>
      {children}
    </Wrapper>
  );
};
